<template>
  <div class="social-share" ref="socialRef">
    <button
      v-if="!(smallscreen && inHome)"
      class="social-share__share-btn"
      @click="socialOpen = !socialOpen"
      :aria-label="$t('general.ctas.openSocial')"
    >
      <img :src="`${$baseUrl}icons/share.svg`" alt="" role="presentation" />
    </button>

    <p v-if="smallscreen && inHome" class="social-share__label">
      {{ $t("general.ctas.share") }}
    </p>
    <Transition name="fade">
      <div
        :class="['social-share-menu', { 'top-modal': topModal }]"
        v-if="socialOpen || (smallscreen && inHome)"
      >
        <div
          v-for="item of $tm(`metaContent.socialMedia`)"
          class="social-share-menu__button"
          :key="item.social"
        >
          <ShareNetwork
            :network="item.social"
            :url="socialUrl"
            :title="$t(`metaContent.${route.params.page}.title`)"
            :description="$t(`metaContent.${route.params.page}.description`)"
            :aria-label="item.social"
            @open="onSocialShare(item.social)"
          >
            <img :src="`${$baseUrl}icons/${item.social}.svg`" alt="" />
          </ShareNetwork>
        </div>

        <a
          :href="`mailto:subject=${$t(
            'pages.home[0].content.title'
          )}&body=${windowLink}`"
          class="social-share-menu__button"
          :aria-label="$t('general.aria.email')"
          @click.native="emailLink"
        >
          <img :src="`${$baseUrl}icons/email.svg`" alt="" role="presentation" />
        </a>

        <button
          class="social-share-menu__button"
          :aria-label="$t('general.aria.copy')"
          @click="copyLink"
        >
          <img
            :src="`${$baseUrl}icons/share-link.svg`"
            alt=""
            role="presentation"
          />
        </button>

        <Transition name="fade">
          <div
            v-if="copied"
            :class="['social-share-menu__copied', { home: inHome }]"
          >
            {{ $t("general.ctas.copied") }}
          </div>
        </Transition>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref } from "vue";

import { useRoute } from "vue-router";
import { onClickOutside } from "@vueuse/core";
import { pushData } from "@/utilities/analytics-functions.js";

defineProps({
  smallscreen: Boolean,
  topModal: Boolean,
  inHome: Boolean,
});

// TODO: what should this be?
const socialUrl = window.location.href;
const windowLink = window.location.href;

const socialOpen = ref(false);
const route = useRoute();
const socialRef = ref(null);
const copied = ref(false);

onClickOutside(socialRef, () => (socialOpen.value = false));

function emailLink() {
  onSocialShare("email");
}

function copyLink() {
  navigator.clipboard.writeText(window.location.href);
  copied.value = true;

  onSocialShare("link");

  setTimeout(() => (copied.value = false), 2000);
}

function onSocialShare(socialItem) {
  pushData({
    event: "linkShare",
    eventType: socialItem,
    componentType: "SocialSharing",
    componentTitle: "",
    componentLink: "",
    componentLinkText: socialItem,
  });
}
</script>

<style lang="scss" scoped>
.social-share {
  position: relative;

  &__share-btn {
    display: block;
    // line-height: 0;

    img {
      display: block;
    }
  }

  &__label {
    @include overlay-s;

    margin-bottom: 0.5rem;
    margin-top: 2rem;
  }

  .mobile &,
  .tablet & {
    justify-content: center;
    gap: 1.75rem;

    .social-share-menu {
      &__button {
        border-color: var(--white);
      }
    }
  }

  .mobile & {
    :deep(.round-icon-button) {
      width: fit-content;
    }

    .social-share-menu {
      top: unset;
      bottom: toRem(40);
      right: 0;

      &::before {
        top: unset;
        bottom: toRem(-4);
      }
    }
  }

  &-menu {
    position: relative;
    width: fit-content;
    margin: 0 auto;
    background-color: var(--background-base);
    padding: 0.75rem 1.5rem 0.5rem;
    display: flex;
    gap: 1rem;
    border-radius: 0.5rem;
    border: 1px solid var(--white-40);
    z-index: 6;

    .desktop &,
    .large & {
      position: absolute;
      top: calc(100% + 0.5rem);
      right: 0;
    }

    &.top-modal {
      position: absolute;
      bottom: calc(100% + 0.5rem);
      right: 0;
    }

    &__button {
      color: var(--bg-dark);
      text-align: center;
      width: 100%;
      transition: color 0.3s ease;

      img {
        .mobile &,
        .tablet & {
          width: 1.5rem;
        }
      }

      &:hover {
        opacity: 0.8;
      }
    }

    &__copied {
      @include elevation-hover;

      position: absolute;
      background-color: var(--white);
      color: var(--background-base);
      padding: toRem(6) 0.75rem;
      border-radius: 0.5rem;
      right: 0;
      top: calc(100% + 0.25rem);
      font-size: 0.875rem;

      .mobile &,
      .tablet & {
        &:not(.home) {
          top: initial;
          bottom: calc(100% + 0.25rem);
        }
      }
    }
  }
}
</style>
